import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { WhatsAppButtonWithTemplates, WhatsAppIcon } from '@/components/WhatsAppButton';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Upload, 
  Mail,
  Phone,
  Building,
  MapPin,
  User,
  AlertTriangle,
  MessageCircle
} from 'lucide-react';

export default function ContactsPage() {
  const { api } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    address: '',
    city: '',
    district: '',
    category: '',
    lat: '',
    lng: '',
    notes: '',
    tags: []
  });

  useEffect(() => {
    loadContacts();
  }, [searchQuery]);

  const loadContacts = async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await api().get(`/contacts${params}`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contactos');
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicates = async () => {
    if (!form.email && !form.phone) return;
    
    try {
      const params = new URLSearchParams();
      if (form.email) params.append('email', form.email);
      if (form.phone) params.append('phone', form.phone);
      
      const response = await api().get(`/contacts/check-duplicates?${params}`);
      if (response.data.duplicates.length > 0) {
        setDuplicateWarning(response.data.duplicates[0]);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Error checking duplicates', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...form,
      lat: form.lat ? parseFloat(form.lat) : null,
      lng: form.lng ? parseFloat(form.lng) : null
    };
    
    try {
      if (editingContact) {
        await api().put(`/contacts/${editingContact.id}`, data);
        toast.success('Cliente atualizado!');
      } else {
        await api().post('/contacts', data);
        toast.success('Cliente criado!');
      }
      
      setShowModal(false);
      resetForm();
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar cliente');
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Eliminar contacto "${contact.name}"?`)) return;
    
    try {
      await api().delete(`/contacts/${contact.id}`);
      toast.success('Contacto eliminado!');
      loadContacts();
    } catch (error) {
      toast.error('Erro ao eliminar contacto');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
      position: contact.position || '',
      address: contact.address || '',
      city: contact.city || '',
      district: contact.district || '',
      category: contact.category || '',
      lat: contact.lat?.toString() || '',
      lng: contact.lng?.toString() || '',
      notes: contact.notes || '',
      tags: contact.tags || []
    });
    setShowModal(true);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api().post('/contacts/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.imported} contactos importados! ${response.data.duplicates} duplicados ignorados.`);
      setShowImportModal(false);
      loadContacts();
    } catch (error) {
      toast.error('Erro ao importar ficheiro');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      address: '',
      city: '',
      district: '',
      category: '',
      lat: '',
      lng: '',
      notes: '',
      tags: []
    });
    setEditingContact(null);
    setDuplicateWarning(null);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada neste browser');
      return;
    }
    
    toast.info('A obter localização...');
    
    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm({
          ...form,
          lat: position.coords.latitude.toFixed(6),
          lng: position.coords.longitude.toFixed(6)
        });
        toast.success('Localização obtida com sucesso!');
      },
      (error) => {
        let errorMessage = 'Não foi possível obter a localização';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permissão de localização negada. Por favor, permita o acesso à localização nas definições do browser.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Informação de localização indisponível.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tempo de espera excedido ao obter localização.';
            break;
        }
        toast.error(errorMessage);
      },
      options
    );
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar contactos..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="contacts-search"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportModal(true)} data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" />
            Importar CSV
          </Button>
          <Button 
            className="bg-indigo-800 hover:bg-indigo-900"
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="new-contact-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Contacto
          </Button>
        </div>
      </div>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-16">
              <User className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold font-[Manrope]">Nenhum contacto encontrado</h3>
              <p className="text-muted-foreground mt-1">Comece por adicionar o primeiro contacto</p>
              <Button 
                className="mt-4 bg-indigo-800 hover:bg-indigo-900"
                onClick={() => { resetForm(); setShowModal(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Contacto
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="block sm:hidden space-y-3 p-3">
                {contacts.map((contact) => (
                  <div key={contact.id} className="p-3 border rounded-lg bg-white" data-testid={`contact-card-${contact.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-indigo-800">
                            {contact.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{contact.name}</p>
                          {contact.company && (
                            <p className="text-xs text-muted-foreground truncate">{contact.company}</p>
                          )}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(contact)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(contact)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="mt-3 space-y-1 text-sm">
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-3 w-3" />
                          {contact.phone}
                        </a>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-blue-600">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{contact.email}</span>
                        </a>
                      )}
                      {contact.address && (
                        <p className="flex items-center gap-2 text-slate-500 text-xs">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{contact.address}</span>
                        </p>
                      )}
                    </div>
                    {/* WhatsApp Button - Mobile */}
                    {contact.phone && (
                      <div className="mt-3 pt-3 border-t">
                        <WhatsAppButtonWithTemplates
                          phone={contact.phone}
                          clientName={contact.name}
                          size="sm"
                          showText={true}
                          className="w-full justify-center"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[250px]">NOME</TableHead>
                      <TableHead>EMAIL</TableHead>
                      <TableHead>TELEFONE</TableHead>
                      <TableHead>EMPRESA</TableHead>
                      <TableHead className="w-[100px]">AÇÕES</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-indigo-800">
                                {contact.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{contact.name}</p>
                              {contact.position && (
                                <p className="text-xs text-muted-foreground">{contact.position}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {contact.email}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {contact.phone}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          {contact.company && (
                            <div className="flex items-center gap-1">
                              <Building className="h-3 w-3 text-muted-foreground" />
                              {contact.company}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {contact.phone && (
                              <WhatsAppButtonWithTemplates
                                phone={contact.phone}
                                clientName={contact.name}
                                size="icon"
                                showText={false}
                                className="h-8 w-8"
                              />
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`contact-actions-${contact.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(contact)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(contact)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingContact ? 'Editar Contacto' : 'Novo Contacto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {duplicateWarning && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Possível duplicado encontrado!</p>
                    <p>Já existe um contacto com este {duplicateWarning.email === form.email ? 'email' : 'telefone'}: {duplicateWarning.name}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    data-testid="contact-company-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    onBlur={checkDuplicates}
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    onBlur={checkDuplicates}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="position">Cargo</Label>
                <Input
                  id="position"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                  data-testid="contact-position-input"
                />
              </div>

              {/* Category - Important for AI route planning */}
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Ex: Restaurante, Loja, Supermercado, Hotel..."
                  data-testid="contact-category-input"
                />
                <p className="text-xs text-muted-foreground">
                  Usado pela IA para planear rotas por tipo de cliente
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Morada</Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="contact-address-input"
                />
              </div>

              {/* City and District */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Ex: Lisboa, Porto..."
                    data-testid="contact-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">Distrito/Zona</Label>
                  <Input
                    id="district"
                    value={form.district}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                    placeholder="Ex: Baixa, Centro..."
                    data-testid="contact-district-input"
                  />
                </div>
              </div>

              {/* Location Fields */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Localização (GPS)</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={getCurrentLocation}
                    data-testid="get-location-btn"
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Obter Localização
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Latitude"
                    type="number"
                    step="any"
                    value={form.lat}
                    onChange={(e) => setForm({ ...form, lat: e.target.value })}
                    data-testid="contact-lat-input"
                  />
                  <Input
                    placeholder="Longitude"
                    type="number"
                    step="any"
                    value={form.lng}
                    onChange={(e) => setForm({ ...form, lng: e.target.value })}
                    data-testid="contact-lng-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Necessário para planificação de rotas
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="contact-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-contact-btn">
                {editingContact ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Importar Contactos</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Carregue um ficheiro CSV com as colunas: name, email, phone, company, position, address
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              ref={fileInputRef}
              className="hidden"
            />
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
              data-testid="select-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Selecionar Ficheiro CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
